import React, { useState, Component } from 'react';
/**
 * AnswerComparison — Shows user answer vs correct answer in various modes.
 *
 * Modes: full (side-by-side cards), compact (stacked), inline (minimal).
 * Supports LaTeX rendering and long-answer truncation.
 *
 * Co-located in practice/ domain (CL-1 s64).
 */
import { motion } from 'framer-motion';
import { Check, X, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
// ── Types ────────────────────────────────────────────────────
interface AnswerComparisonProps {
  userAnswer: string;
  correctAnswer: string;
  isCorrect: boolean;
  mode?: 'full' | 'compact' | 'inline';
}
// ── Truncation Helper ────────────────────────────────────────
const TRUNCATE_LIMIT = 50;
function TruncatedAnswer({
  answer,
  variant
}: {
  answer: string;
  variant: 'correct' | 'incorrect';
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const needsTruncation = answer.length > TRUNCATE_LIMIT;
  const displayText = needsTruncation && !isExpanded ? answer.slice(0, TRUNCATE_LIMIT) + '...' : answer;
  const styles = variant === 'correct' ? {
    bg: 'bg-emerald-50 border-emerald-200',
    text: 'text-emerald-900',
    btn: 'text-emerald-600',
    border: 'border-emerald-100'
  } : {
    bg: 'bg-rose-50 border-rose-200',
    text: 'text-rose-900',
    btn: 'text-rose-600',
    border: 'border-rose-100'
  };
  return <div className={`rounded-xl border ${styles.bg} overflow-hidden`} data-id="element-11150" data-mp="41cn6zv0m12l">
      <div className="px-3 py-2.5" data-id="element-11151" data-mp="80v4zmrjhw17">
        <div className={`font-bold text-sm ${styles.text}`} data-id="element-11152" data-mp="826aps4jpduy">
          <LatexRenderer content={displayText} data-id="element-11153" data-mp="d0hh1svmt68f" />
        </div>
      </div>
      {needsTruncation && <button onClick={() => setIsExpanded(!isExpanded)} className={`w-full px-3 py-1.5 text-[10px] font-bold ${styles.btn} bg-white/50 border-t ${styles.border} flex items-center justify-center gap-1 hover:bg-white/80 transition-colors`} data-id="element-11154" data-mp="hrzliqwv5oy5">
        
          {isExpanded ? <>
              Toon minder <ChevronUp className="w-3 h-3" data-id="element-11155" data-mp="slj3xj7uqprj" />
            </> : <>
              Toon meer <ChevronDown className="w-3 h-3" data-id="element-11156" data-mp="nmzhlhpcjbyn" />
            </>}
        </button>}
    </div>;
}
// ── Component ────────────────────────────────────────────────
export function AnswerComparison({
  userAnswer,
  correctAnswer,
  isCorrect,
  mode = 'full'
}: AnswerComparisonProps) {
  // ── Inline Mode ──
  if (mode === 'inline') {
    if (isCorrect) {
      return <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="flex justify-center" data-id="element-11157" data-mp="9nzw2ix4rd6p">
          
          <div className="inline-flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-3 shadow-sm max-w-full" data-id="element-11158" data-mp="dos1t26smzy8">
            <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0" data-id="element-11159" data-mp="1u20htzlemgb1">
              <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-11160" data-mp="1p3fehrauvgid" />
            </div>
            <div className="font-bold text-emerald-900 text-base truncate max-w-[220px]" data-id="element-11161" data-mp="1xjwlrjuqcdtp">
              <LatexRenderer content={userAnswer.length > TRUNCATE_LIMIT ? userAnswer.slice(0, TRUNCATE_LIMIT) + '...' : userAnswer} data-id="element-11162" data-mp="21nm43rkt9z6a" />
              
            </div>
          </div>
        </motion.div>;
    }
    return <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0,
      x: [0, -6, 5, -4, 3, -1, 0]
    }} transition={{
      duration: 0.3,
      ease: 'easeOut',
      x: {
        duration: 0.4,
        delay: 0.1
      }
    }} className="space-y-2.5 px-2" data-id="element-11163" data-mp="1smnzp86gnpn6">
        
        <div className="flex items-start gap-2" data-id="element-11164" data-mp="26l4dfj2qqget">
          <div className="w-5 h-5 rounded-full bg-rose-200 flex items-center justify-center text-rose-700 flex-shrink-0 mt-2.5" data-id="element-11165" data-mp="2bk7aag8khgbp">
            <X className="w-3.5 h-3.5" strokeWidth={3} data-id="element-11166" data-mp="2z9jgd78ll89s" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-11167" data-mp="341jovo2qh9hz">
            <p className="text-[10px] font-bold uppercase text-rose-500 mb-1" data-id="element-11168" data-mp="342mfrw023bxw">
              Jouw antwoord
            </p>
            <TruncatedAnswer answer={userAnswer} variant="incorrect" data-id="element-11169" data-mp="1fcvuvd0dp42x" />
          </div>
        </div>
        <div className="flex items-start gap-2" data-id="element-11170" data-mp="1bfzstd0weuxm">
          <div className="w-5 h-5 rounded-full bg-emerald-200 flex items-center justify-center text-emerald-700 flex-shrink-0 mt-2.5" data-id="element-11171" data-mp="16flgvjw6f0ub">
            <Check className="w-3.5 h-3.5" strokeWidth={3} data-id="element-11172" data-mp="11hk0j6q4xfmj" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-11173" data-mp="1z1wkl3f2xhvc">
            <p className="text-[10px] font-bold uppercase text-emerald-600 mb-1" data-id="element-11174" data-mp="1u44qc2qypjnz">
              Correct
            </p>
            <TruncatedAnswer answer={correctAnswer} variant="correct" data-id="element-11175" data-mp="1pcv1h4d8mkd5" />
          </div>
        </div>
      </motion.div>;
  }
  // ── Compact Mode ──
  if (mode === 'compact') {
    return <div className="grid gap-2" data-id="element-11176" data-mp="2htpc0tei2v0y">
        <div className={`p-3 rounded-xl border-2 ${isCorrect ? 'border-emerald-200 bg-emerald-50' : 'border-rose-100 bg-rose-50'}`} data-id="element-11177" data-mp="2huqydu0yv3xz">
          
          <div className="text-[10px] font-black uppercase mb-1 opacity-60" data-id="element-11178" data-mp="2d0w98b2ldwri">
            Jouw antwoord
          </div>
          <div className="font-bold text-slate-900 text-sm" data-id="element-11179" data-mp="2pd8923kkwosj">
            <LatexRenderer content={userAnswer} data-id="element-11180" data-mp="2pdgq87nqig9j" />
          </div>
        </div>
        {!isCorrect && <div className="p-3 rounded-xl border-2 border-emerald-200 bg-emerald-50" data-id="element-11181" data-mp="2uaqdbgnnifu7">
            <div className="text-[10px] font-black uppercase mb-1 text-emerald-700" data-id="element-11182" data-mp="2zc5xz7lx2kc2">
              Juiste antwoord
            </div>
            <div className="font-bold text-emerald-900 text-sm" data-id="element-11183" data-mp="33awwnj616a1a">
              <LatexRenderer content={correctAnswer} data-id="element-11184" data-mp="38arnxntpx1mv" />
            </div>
          </div>}
      </div>;
  }
  // ── Full Mode ──
  if (isCorrect) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.97
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.35,
      ease: [0.22, 1, 0.36, 1]
    }} className="bg-emerald-50 rounded-3xl p-6 border-2 border-emerald-200 shadow-sm" data-id="element-11185" data-mp="3i1w2imfhekh6">
        
        <div className="flex items-center gap-4" data-id="element-11186" data-mp="3r07q0fl1x25e">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: [0, 1.2, 1]
        }} transition={{
          delay: 0.1,
          duration: 0.4,
          times: [0, 0.6, 1]
        }} className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center shadow-md flex-shrink-0" data-id="element-11187" data-mp="1ryvjdis47ggd">
            
            <Check className="w-7 h-7 text-white" strokeWidth={3} data-id="element-11188" data-mp="1vy48298aso52" />
          </motion.div>
          <div data-id="element-11189" data-mp="2amgxsogslar4">
            <div className="text-xs font-black uppercase tracking-wider text-emerald-600 mb-1" data-id="element-11190" data-mp="2aqehbb7a84bj">
              Jouw antwoord
            </div>
            <div className="font-black text-2xl text-emerald-900" data-id="element-11191" data-mp="2jp9r0x9jbwjl">
              <LatexRenderer content={userAnswer} data-id="element-11192" data-mp="2fneqvw3cbp7e" />
            </div>
          </div>
        </div>
      </motion.div>;
  }
  return <div className="flex flex-col md:flex-row gap-3 items-stretch" data-id="element-11193" data-mp="pafjv6zdobz9">
      <motion.div initial={{
      opacity: 0,
      x: -16
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} className="flex-1" data-id="element-11194" data-mp="pcs8g2zn5kla">
        
        <div className="bg-rose-50 rounded-3xl p-6 border-2 border-rose-200 h-full shadow-sm" data-id="element-11195" data-mp="f6sf1qrw46je">
          <div className="flex items-center gap-3 mb-3" data-id="element-11196" data-mp="1fndgj4yp46f">
            <div className="w-9 h-9 rounded-xl bg-rose-200 flex items-center justify-center text-rose-700 shadow-sm flex-shrink-0" data-id="element-11197" data-mp="1f407fq9b3yq">
              <X className="w-5 h-5" strokeWidth={3} data-id="element-11198" data-mp="3nmtvkwx8wsu8" />
            </div>
            <span className="text-xs font-black uppercase tracking-wider text-rose-600" data-id="element-11199" data-mp="3nliu53r9vx6a">
              Jouw antwoord
            </span>
          </div>
          <div className="font-black text-2xl text-rose-900 pl-12" data-id="element-11200" data-mp="3emoypgpohcub">
            <LatexRenderer content={userAnswer} data-id="element-11201" data-mp="3eoi9n87bqj84" />
          </div>
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      scale: 0
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 0.15,
      duration: 0.3
    }} className="hidden md:flex items-center justify-center flex-shrink-0" data-id="element-11202" data-mp="3jn39h6inz0w1">
        
        <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center" data-id="element-11203" data-mp="2qczt4l0r15ma">
          <ArrowRight className="w-5 h-5 text-slate-400" strokeWidth={2.5} data-id="element-11204" data-mp="2lfzu2u2bh34o" />
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      x: 16
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.1,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} className="flex-1" data-id="element-11205" data-mp="2gmeu4wh5xz2u">
        
        <div className="bg-emerald-50 rounded-3xl p-6 border-2 border-emerald-200 h-full shadow-sm" data-id="element-11206" data-mp="22o7478ijny16">
          <div className="flex items-center gap-3 mb-3" data-id="element-11207" data-mp="1xp3xzkqtvlrm">
            <div className="w-9 h-9 rounded-xl bg-emerald-200 flex items-center justify-center text-emerald-700 shadow-sm flex-shrink-0" data-id="element-11208" data-mp="1xrq55j9t3z9b">
              <Check className="w-5 h-5" strokeWidth={3} data-id="element-11209" data-mp="3s46h90paa7m3" />
            </div>
            <span className="text-xs font-black uppercase tracking-wider text-emerald-600" data-id="element-11210" data-mp="3o235sc2m730t">
              Het juiste antwoord
            </span>
          </div>
          <div className="font-black text-2xl text-emerald-900 pl-12" data-id="element-11211" data-mp="19tsh2193crus">
            <LatexRenderer content={correctAnswer} data-id="element-11212" data-mp="1eslj8xixgwfn" />
          </div>
        </div>
      </motion.div>
    </div>;
}